import React, { useState } from 'react';
import type { InventoryItem } from '../../../../../shared/CharacterTypes';
import { ItemIcon } from '../../../components/ItemIcon';
import type { BankItemNotice } from '../../../hooks/useBank';
import { isTouchDevice } from '../../../mobile/touchControlsFlag';
import { TomeButton } from '../../../primitives/tome';

/**
 * The Bank's visible way to move an item, for a touch screen or a controller
 * that has no double-click: the selected item, how many (a stepper, a typed
 * amount, All; the whole stack to begin with), and Deposit or Withdraw. The
 * action waits while the server decides, and its answer shows beneath.
 */
export type BankSide = 'bag' | 'vault';

const SIDE_COPY: Record<BankSide, { action: string; place: string }> = {
  bag: { action: 'Deposit', place: 'in your bag' },
  vault: { action: 'Withdraw', place: 'in the vault' },
};

export interface BankTransferBarProps {
  selected: { side: BankSide; item: InventoryItem } | null;
  pending: boolean;
  notice: BankItemNotice | null;
  onTransfer: (side: BankSide, item: InventoryItem, quantity: number) => void;
}

function TransferForm({ side, item, pending, onTransfer }: {
  side: BankSide; item: InventoryItem; pending: boolean; onTransfer: BankTransferBarProps['onTransfer'];
}): React.ReactElement {
  const stack = Math.max(1, item.quantity ?? 1);
  const [text, setText] = useState(String(stack));
  const shown = text === '' ? '' : String(Math.min(Number(text), stack));
  const quantity = Number(shown);
  const step = (by: number) => setText(String(Math.min(stack, Math.max(1, quantity + by))));
  const copy = SIDE_COPY[side];
  const label = stack > 1 && quantity > 0 ? `${copy.action} ${quantity} ${item.name}` : `${copy.action} ${item.name}`;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (quantity > 0 && !pending) onTransfer(side, item, quantity);
  };

  return <form className="bank-transfer-form" data-side={side} onSubmit={submit} noValidate>
    <span className="bank-transfer-icon" aria-hidden="true"><ItemIcon itemId={item.id} size={30} /></span>
    <span className="bank-transfer-item">
      <strong>{item.name}</strong>
      <small>{stack} {copy.place}</small>
    </span>
    {stack > 1 && <span className="bank-transfer-amount">
      <TomeButton tone="secondary" cut="clean" aria-label="One fewer" disabled={quantity <= 1} onClick={() => step(-1)}>−</TomeButton>
      <input className="bank-purse-input" type="text" inputMode="numeric" autoComplete="off" spellCheck={false}
        aria-label="How many to move" value={shown} onChange={event => setText(event.target.value.replace(/\D/g, ''))} />
      <TomeButton tone="secondary" cut="clean" aria-label="One more" disabled={quantity >= stack} onClick={() => step(1)}>+</TomeButton>
      <TomeButton tone="secondary" cut="clean" aria-label="Move the whole stack" disabled={quantity === stack} onClick={() => setText(String(stack))}>All</TomeButton>
    </span>}
    <TomeButton type="submit" cut="clean" aria-label={label} disabled={quantity <= 0 || pending}>{copy.action}</TomeButton>
  </form>;
}

export const BankTransferBar: React.FC<BankTransferBarProps> = ({ selected, pending, notice, onTransfer }) =>
  <div className="bank-transfer" data-testid="bank-transfer">
    {selected
      ? <TransferForm key={`${selected.side}:${selected.item.id}`} {...selected} pending={pending} onTransfer={onTransfer} />
      : <p className="bank-transfer-hint" data-testid="bank-transfer-hint">{isTouchDevice()
        ? 'Tap an item to choose how many to move.'
        : 'Select an item to choose how many to move, or double-click it to move the whole stack.'}</p>}
    <p className="bank-purse-note" data-testid="bank-transfer-note" data-tone={notice?.tone ?? 'ok'} role="status" aria-live="polite">{notice?.text ?? ''}</p>
  </div>;
